import { Component, inject, NgZone, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, RouterModule } from "@angular/router";
import {
  AuthService,
  BACKOFFICE_UNAUTHORIZED_MESSAGE,
} from "../../../shared/services/auth.service";
import { environment } from "../../../../environments/environment";

interface PendingLegalVersion {
  id: number;
  type: string;
  version: string;
  change_summary: string[];
  created_at?: string;
}

@Component({
  selector: "app-login",
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private route = inject(ActivatedRoute);

  loginForm = this.fb.group({
    email: ["", [Validators.required, Validators.email]],
    password: ["", [Validators.required, Validators.minLength(6)]],
  });

  showPassword = false;
  legalModalOpen = false;
  pendingLegalVersions: PendingLegalVersion[] = [];
  legalLoading = false;
  loginError: string | null = null;
  socialLoading = false;
  socialError: string | null = null;
  otpStep = false;
  otpEmail = "";
  otpCode = "";
  otpSending = false;
  otpCooldownSec = 0;

  private pendingSocialProvider: string | null = null;
  private pendingSocialIdToken: string | null = null;
  private otpCooldownHandle: ReturnType<typeof setInterval> | null = null;

  ngOnInit() {
    this.route.queryParamMap.subscribe((params) => {
      if (params.get("unauthorized") === "1") {
        this.loginError = BACKOFFICE_UNAUTHORIZED_MESSAGE;
      }
    });

    this.loginForm.valueChanges.subscribe(() => {
      if (
        this.loginError &&
        this.loginError !== BACKOFFICE_UNAUTHORIZED_MESSAGE
      ) {
        this.loginError = null;
      }
    });

    const google = (window as any).google;
    if (!google?.accounts?.id) return;

    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      use_fedcm_for_prompt: false,
      callback: (response: any) => {
        this.ngZone.run(() => {
          if (!response?.credential) {
            this.socialLoading = false;
            this.socialError = "No se recibió token de Google.";
            return;
          }
          this.runSocialLogin("google", response.credential);
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.clearOtpCooldown();
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  onSubmit() {
    this.loginError = null;
    if (this.loginForm.valid) {
      this.runLogin();
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  closeLegalModal() {
    this.legalModalOpen = false;
    this.pendingLegalVersions = [];
    this.legalLoading = false;
    this.pendingSocialProvider = null;
    this.pendingSocialIdToken = null;
  }

  confirmLegalAcceptance() {
    const ids = this.pendingLegalVersions.map((v) => v.id);
    if (this.pendingSocialProvider && this.pendingSocialIdToken) {
      this.runSocialLogin(this.pendingSocialProvider, this.pendingSocialIdToken, ids);
    } else {
      this.runLogin(ids);
    }
  }

  loginWithGoogle() {
    this.socialError = null;
    this.socialLoading = true;

    const google = (window as any).google;
    if (!google?.accounts?.id) {
      this.socialLoading = false;
      this.socialError = "Google Sign-In no está disponible. Recargá la página.";
      return;
    }

    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      use_fedcm_for_prompt: false,
      callback: (response: any) => {
        this.ngZone.run(() => {
          if (!response?.credential) {
            this.socialLoading = false;
            this.socialError = "No se recibió token de Google.";
            return;
          }
          this.runSocialLogin("google", response.credential);
        });
      },
    });

    google.accounts.id.prompt((notification: any) => {
      this.ngZone.run(() => {
        if (notification.isNotDisplayed()) {
          this.socialLoading = false;
          this.socialError = `Google: ${notification.getNotDisplayedReason()}`;
        } else if (notification.isSkippedMoment()) {
          this.socialLoading = false;
          this.socialError = `Google: ${notification.getSkippedReason()}`;
        }
      });
    });
  }

  async loginWithApple() {
    this.socialError = null;
    this.socialLoading = true;
    try {
      const appleID = (window as any).AppleID;
      appleID.auth.init({
        clientId: environment.appleClientId,
        scope: "name email",
        redirectURI: environment.appleRedirectUri,
        usePopup: true,
      });
      const response = await appleID.auth.signIn();
      const idToken = response?.authorization?.id_token;
      if (!idToken) throw new Error("No se recibió token de Apple.");
      this.runSocialLogin("apple", idToken);
    } catch (err: any) {
      this.socialLoading = false;
      this.socialError = err?.message ?? "Error al iniciar sesión con Apple.";
    }
  }

  private runSocialLogin(provider: string, idToken: string, acceptedVersionIds?: number[]) {
    this.socialLoading = true;
    this.authService.loginWithSocial(provider, idToken, acceptedVersionIds).subscribe({
      next: () => {
        this.socialLoading = false;
        this.closeLegalModal();
      },
      error: (err: any) => {
        this.socialLoading = false;
        const body = err?.error ?? err;
        if (body?.int_code === 10201 && body?.data?.pendingVersions?.length) {
          this.pendingSocialProvider = provider;
          this.pendingSocialIdToken = idToken;
          this.pendingLegalVersions = body.data.pendingVersions;
          this.legalModalOpen = true;
          return;
        }
        this.socialError = this.resolveApiErrorMessage(body);
      },
    });
  }

  private runLogin(acceptedVersionIds?: number[]) {
    const { email, password } = this.loginForm.getRawValue();
    const credentials: {
      email: string;
      password: string;
      acceptedVersionIds?: number[];
    } = { email: email ?? "", password: password ?? "" };
    if (acceptedVersionIds?.length) {
      credentials.acceptedVersionIds = acceptedVersionIds;
    }

    this.loginError = null;
    this.legalLoading = true;
    this.authService.login(credentials).subscribe({
      next: (response: any) => {
        this.legalLoading = false;
        if (response?.data?.requiresEmailOtp) {
          this.otpStep = true;
          this.otpEmail = response.data.email ?? credentials.email;
          this.otpCode = "";
          this.startOtpCooldown(response.data.retryAfterSec);
          this.closeLegalModal();
          return;
        }
        if (!response?.data?.token) {
          this.loginError = BACKOFFICE_UNAUTHORIZED_MESSAGE;
        }
        this.closeLegalModal();
      },
      error: (error: any) => {
        this.legalLoading = false;
        const body = error?.error ?? error;
        if (body?.int_code === 10201 && body?.data?.pendingVersions?.length) {
          this.pendingLegalVersions = body.data.pendingVersions;
          this.legalModalOpen = true;
          return;
        }
        this.loginError = this.resolveApiErrorMessage(body);
      },
    });
  }

  submitOtp(): void {
    const code = this.otpCode.trim();
    if (!/^\d{6}$/.test(code)) {
      this.loginError = "Ingresa el código de 6 dígitos que enviamos a tu correo.";
      return;
    }
    this.loginError = null;
    this.legalLoading = true;
    this.authService.verifyEmailOtp(this.otpEmail, code).subscribe({
      next: () => {
        this.legalLoading = false;
      },
      error: (error: any) => {
        this.legalLoading = false;
        this.loginError = this.resolveApiErrorMessage(error?.error ?? error);
      },
    });
  }

  resendOtp(): void {
    if (this.otpSending || this.otpCooldownSec > 0) {
      return;
    }
    this.otpSending = true;
    this.loginError = null;
    this.authService.resendEmailOtp(this.otpEmail).subscribe({
      next: (response: any) => {
        this.otpSending = false;
        this.startOtpCooldown(response?.data?.retryAfterSec);
        if (response?.data?.otpSent === false && response?.message) {
          this.loginError = response.message;
        }
      },
      error: (error: any) => {
        this.otpSending = false;
        this.loginError = this.resolveApiErrorMessage(error?.error ?? error);
      },
    });
  }

  backToPassword(): void {
    this.otpStep = false;
    this.otpCode = "";
    this.loginError = null;
  }

  private startOtpCooldown(seconds?: number): void {
    this.clearOtpCooldown();
    const wait = Number(seconds);
    this.otpCooldownSec = Number.isFinite(wait) && wait > 0 ? Math.ceil(wait) : 60;
    this.otpCooldownHandle = setInterval(() => {
      this.otpCooldownSec -= 1;
      if (this.otpCooldownSec <= 0) {
        this.clearOtpCooldown();
      }
    }, 1000);
  }

  private clearOtpCooldown(): void {
    if (this.otpCooldownHandle) {
      clearInterval(this.otpCooldownHandle);
      this.otpCooldownHandle = null;
    }
    this.otpCooldownSec = 0;
  }

  /** Prefer nested detail; keep top-level message when it adds context (e.g. lockout). */
  private resolveApiErrorMessage(body: any): string {
    const top =
      typeof body?.message === "string" ? body.message.trim() : "";
    const detail =
      typeof body?.error?.message === "string"
        ? body.error.message.trim()
        : "";
    const topIsGeneric = !top || top.toLowerCase() === "error";

    if (detail && topIsGeneric) return detail;
    if (detail && top && detail !== top) return `${top} ${detail}`;
    return detail || top || "Error al iniciar sesión. Intente nuevamente.";
  }
}
